import React, { useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CartSummary from "./CartSummary";
import { ImageURL } from "../../constants";
import defaultImage from "../../assets/contactsvg.svg";
import { useCart } from "react-use-cart";
import { useTranslation } from "react-i18next";

const CartPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { items, cartTotal, removeItem, updateItemQuantity } = useCart();

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {items.map((row, index) => (
        <Box key={`box-${index}`}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1.5, sm: 2 },
              py: { xs: 1.5, sm: 2 },
              px: { xs: 1, sm: 2 },
            }}
          >
            {/* Image */}
            <Box sx={{ flexShrink: 0 }}>
              <img
                src={row?.img ? `${ImageURL}${row.img}` : defaultImage}
                alt={row?.name}
                style={{
                  width: "70px",
                  height: "70px",
                  objectFit: "contain",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                }}
              />
            </Box>

            {/* Name + Quantity */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.82rem" , md: "1.2rem" },
                  fontFamily: '"Poppins", sans-serif',
                  color: "#2858a3",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  mb: 1,
                }}
              >
                {row?.name}
              </Typography>

              {/* Quantity Controls */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  border: "1px solid #e5e7eb",
                  borderRadius: "999px",
                  overflow: "hidden",
                  px: 0.5,
                  py: 0.3,
                  gap: 0.5,
                  background: "#f9fafb",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => updateItemQuantity(row.id, row.quantity - 1)}
                  sx={{
                    width: 26, height: 26,
                    background: "#2858A3",
                    borderRadius: "50%",
                    "&:hover": { background: "#02AFF3" },
                    p: 0,
                  }}
                >
                  <Remove sx={{ fontSize: 14, color: "#fff" }} />
                </IconButton>

                <Typography
                  sx={{
                    minWidth: "28px",
                    textAlign: "center",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    fontFamily: '"Poppins", sans-serif',
                    color: "#2858A3",
                  }}
                >
                  {row?.quantity}
                </Typography>

                <IconButton
                  size="small"
                  onClick={() => updateItemQuantity(row.id, row.quantity + 1)}
                  sx={{
                    width: 26, height: 26,
                    background: "#2858A3",
                    borderRadius: "50%",
                    "&:hover": { background: "#02AFF3" },
                    p: 0,
                  }}
                >
                  <Add sx={{ fontSize: 14, color: "#fff" }} />
                </IconButton>
              </Box>
            </Box>

            {/* Price */}
            <Box sx={{ flexShrink: 0, textAlign: "right", minWidth: "80px" }}>
              {row?.quantity === 1 ? (
                <Typography
                  sx={{
                    fontSize: { xs: "0.78rem", sm: "0.85rem" },
                    fontWeight: 600,
                    color: "#2858A3",
                    fontFamily: '"Poppins", sans-serif',
                  }}
                >
                  {row?.price ?? "0.0"} {row?.currency ?? "AED"}
                </Typography>
              ) : (
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      color: "#6b7280",
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    {row?.quantity} × {row?.price ?? "0.0"}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.78rem", sm: "0.85rem" },
                      fontWeight: 600,
                      color: "#2858A3",
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    {row?.itemTotal?.toFixed(2)} {row?.currency ?? "AED"}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Delete */}
            <IconButton
              size="small"
              onClick={() => removeItem(row?.id)}
              sx={{
                flexShrink: 0,
                color: "#9ca3af",
                "&:hover": { color: "#ef4444", background: "#fee2e2" },
                p: "4px",
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
          </Box>
          <Divider />
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "60vh",
        py: { xs: 4, sm: 6, md: 8 },
        px: { xs: 2, sm: 4, md: 6, lg: 10 },
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        gap: { xs: 4, lg: 6 },
        boxSizing: "border-box",
      }}
    >
      {/* Cart Items */}
      <Box
        sx={{
          flex: "1 1 60%",
          minWidth: 0,
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        {/* Cart Header */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: "1px solid #e5e7eb",
            background: "#f9fafb",
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Poppins", sans-serif',
              fontWeight: 600,
              fontSize: { xs: "0.95rem", sm: "1.05rem" },
              color: "#1f2937",
            }}
          >
            {t("drawer.items") || "Shopping Cart"}
            {items.length > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  fontSize: "0.8rem",
                  fontWeight: 400,
                  color: "#6b7280",
                }}
              >
                ({items.length} {items.length === 1 ? "item" : "items"})
              </Box>
            )}
          </Typography>
        </Box>

        {/* Cart Content */}
        <Box sx={{ px: { xs: 1, sm: 2 } }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
              }}
            >
              <CircularProgress size={60} sx={{ color: "#2858A3" }} />
            </Box>
          ) : items?.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: "1.1rem", sm: "1.4rem" },
                  color: "#1f2937",
                  textAlign: "center",
                }}
              >
                {t("cart.empty") || "Your Cart is Empty"}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "0.85rem",
                  color: "#9ca3af",
                }}
              >
                {t("cart.empty_sub") || "Add some products to get started"}
              </Typography>
            </Box>
          ) : (
            content
          )}
        </Box>
      </Box>

      {/* Cart Summary */}
      <Box
        sx={{
          flex: "0 0 auto",
          width: { xs: "100%", lg: "360px" },
          alignSelf: { lg: "flex-start" },
          position: { lg: "sticky" },
          top: { lg: "90px" },
        }}
      >
        <CartSummary cartTotal={Number(cartTotal)} />
      </Box>
    </Box>
  );
};

export default CartPage;

// const Header = (
//     <>
//         <Box className="p-5" style={{ display: 'flex', justifyContent: 'space-between' }}>
//             <Box style={{ display: 'flex' }}>
//                 <ShoppingCartCheckoutIcon className='text-[#2858a3]' style={{ fontWeight: 900, fontSize: '2.5em' }} />
//                 <p style={{ fontWeight: 900, fontSize: 'px' }}>{itemsLength} Item(s)</p>
//             </Box>
//             <IconButton onClick={() => setOpen(false)}>
//                 <CloseIcon />
//             </IconButton>
//         </Box>
//         <Divider />
//     </>
// )

// const handleIncrement = (index) => {
//     setQuantities({
//         ...quantities,
//         [index]: quantities[index] + 1,
//     });
// };

// const handleDecrement = (index) => {
//     setQuantities({
//         ...quantities,
//         [index]: Math.max(1, quantities[index] - 1),
//     });
// };

// const rows = [
//     { id: 1, name: "Peter England Men's Slim Fit Formal Shirt", price: '$35.00', img: "https://shop.activeitzone.com/public/uploads/all/dCJrKJkUT3OHDIuXNYLyyKyNg84zGHued37VlrDW.png" },
//     { id: 2, name: "ASUS ROG Phone 5 Dual Sim S888 Phantom", price: '$42.00', img: "https://shop.activeitzone.com/public/uploads/all/VlFKAwBynDPebbSce8FVsMPhfmu7sjSUapp3JIQw.png" },
//     { id: 3, name: 'Lannister', price: '$45.00', img: "https://shop.activeitzone.com/public/uploads/all/5ywHNpwAY6dNOapwWjUgLoeYsm18ZRwdU5PzXqJM.png" },
//     { id: 4, name: "Microsoft Surface Arc Mouse, - CZV-00001", price: '$16.00', img: "https://shop.activeitzone.com/public/uploads/all/brO9DLQukZXwzHMH9vExLST8L8w31nDk0ZqMxe3k.png" },
// ];

// const initialQuantities = rows?.reduce((acc, row, index) => {
//     acc[index] = 1; // Default quantity
//     return acc;
// }, {});

// const [quantities, setQuantities] = useState(initialQuantities);
